<template>
  <NuxtLink
    :to="route"
    class="card"
  >
    <div class="img">
      <NuxtImg
        v-if="image"
        :src="image"
        alt=""
        width="350"
        height="195"
        loading="lazy"
        quality="80"
        fit="cover"
        sizes="xs:100vw sm:100vw md:350px"
        format="auto"
      />
      <div class="countdown">
        <template v-if="!past && countdown > -1">
          {{ countdown }} {{ pluralize('jour', countdown) }}
          {{ pluralize('restant', countdown) }}<br />pour participer
        </template>
        <template v-else>Du {{ startDate }}<br />au {{ endDate }}</template>
      </div>
    </div>
    <div class="body">
      <p class="title">
        {{ props.contest.title }}
      </p>
      <template v-if="!past && props.contest.prize">
        <hr />
        <div
          class="description"
          v-html="props.contest.prize"
        />
      </template>
    </div>
  </NuxtLink>
</template>

<script setup lang="ts">
import { differenceInDays, format } from 'date-fns'
import type { ContestResponseItem } from '~/types'

const props = withDefaults(
  defineProps<{
    contest: ContestResponseItem
    past?: boolean
  }>(),
  {
    past: false,
    contest: undefined,
  },
)

const startDate = computed(() => {
  return format(
    new Date(
      props.contest.displayed_starts_at || props.contest.contest_start || '',
    ),
    'dd/MM/yyyy',
  )
})

const endDate = computed(() =>
  format(
    new Date(
      props.contest.displayed_ends_at || props.contest.contest_end || '',
    ),
    'dd/MM/yyyy',
  ),
)

const countdown = computed(() => {
  const remainingDays = differenceInDays(
    new Date(
      props.contest.displayed_ends_at || props.contest.contest_end || '',
    ),
    new Date(),
  )

  if (remainingDays === 0) {
    return 1
  }

  return remainingDays || -1
})

const route = computed(() => {
  switch (props.contest.slide_type) {
    case 'wallContest': {
      return `${ROUTES.CONTEST_WALL}/${props.contest.slug}`
    }
    case 'speedContest': {
      return `${ROUTES.CONTEST_RECORD}/${props.contest.slug}`
    }
    case 'props.contest':
    default: {
      return `${ROUTES.CONTEST_SIMPLE}/${props.contest.slug}`
    }
  }
})

const image = computed(() => {
  return props.contest.bg_desktop?.url ?? ''
})
</script>

<style lang="scss" scoped>
.card {
  display: flex;
  flex-direction: column;
  height: 100%;
  color: inherit;
  text-decoration: none;

  &:hover .img img {
    transform: scale(1.3);
  }
}

.img {
  position: relative;
  width: 100%;
  height: 19.5rem;
  overflow: hidden;
  background: #ccc;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.35s ease-in-out;
  }
}

.countdown {
  position: absolute;
  bottom: 0;
  left: 0;
  padding: 1.5rem 3rem;
  color: $white;
  font-family: 'SST Condensed';
  font-size: 1.8rem;
  font-weight: 700;
  line-height: 1.2;
  text-transform: uppercase;
  background-color: $blue;
  border-top: 5px solid $white;
  border-right: 5px solid $white;
}

.body {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 3rem 3rem 2rem;
  color: $dark;
  font-size: 1.5rem;
  border: 1px solid #ccc;
  border-top: 0;

  hr {
    margin: auto 0 1.5rem;
    border-color: #ccc;
    border-top: 0;
  }
}

.title {
  margin-bottom: 1.5rem;
}

.description {
  margin-bottom: 1.5rem;
  font-family: 'SST Condensed';
  font-size: 1.8rem;
  font-weight: 700;
  white-space: pre-line;
}
</style>
